<template>
  <div class="my-10">
    <Modal :show-modal="showSuccess" @cancel="closeModal">
      <template v-slot:header>{{ $t('page.orderSuccess') }}</template>
      <template v-slot:content>
        <div class="w-full">
          <table>
            <thead>
              <tr>
                <th class="border-b text-start border-[#dddddd] pb-2">{{ $t('page.name') }}</th>
                <th class="border-b text-center border-[#dddddd] pb-2">
                  {{ $t('page.quantity') }}
                </th>
                <th class="border-b text-end border-[#dddddd] pb-2">{{ $t('page.price') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in receipt?.cart_items" :key="item.id">
                <td class="border-b text-start py-2">
                  {{ item?.product?.name }} | {{ item?.size }}
                </td>
                <td class="border-b text-center py-2">{{ item?.quantity }}</td>
                <td class="border-b text-end py-2">{{ formatNumber(item?.total_price) }} TMT</td>
              </tr>
            </tbody>
          </table>
          <div class="flex justify-between mt-5">
            <div class="flex flex-col gap-2">
              <p class="text-lg font-light text-main-color text-start">{{ $t('page.delivery') }}</p>
              <p class="text-lg font-bold text-main-color text-start">
                {{ $t('page.totalPrice') }}
              </p>
            </div>
            <div class="flex flex-col gap-2">
              <p class="text-lg font-light text-main-color">{{ receipt?.delivery_price }} TMT</p>
              <p class="text-lg font-bold text-main-color">
                {{ formatNumber(receipt?.total_price) }} TMT
              </p>
            </div>
          </div>
        </div>
      </template>
    </Modal>
    <div class="mb-4 p-4 bg-white shadow rounded">
      <p class="text-2xl font-bold">{{ getFoodCart?.store?.name }}</p>
      <p class="mt-2">{{ $t('page.location') }}: {{ getFoodCart?.store?.location }}</p>
      <div class="flex gap-1 mt-2" v-if="getFoodCart?.store?.work_hours">
        <p>{{ $t('page.workTime') }}:</p>
        <!-- <IconClock/> -->
        {{ getFoodCart?.store?.work_hours[getDay(getFoodCart?.store?.work_hours).toLowerCase()] }}
      </div>
    </div>
    <div class="mb-4 flex flex-col gap-2">
      <FoodCheckoutCard
        v-for="item in getFoodCart?.cart_items"
        :key="item.id"
        :detail="item"
      ></FoodCheckoutCard>
    </div>
    <Form @submit="checkout" :validation-schema="checkoutSchema()">
      <div class="grid grid-cols-2 gap-10">
        <div class="p-5 bg-white rounded flex flex-col gap-5">
          <div class="flex flex-col gap-1">
            <label class="text-start font-bold text-main-color mb-2" for="name"
              >{{ $t('form.fullname') }}<span class="text-red-500">*</span></label
            >
            <Field
              v-model="formData.name"
              class="p-4 w-full z-10 text-main-color bg-white rounded border focus:outline-none text-sm"
              type="name"
              name="name"
              id="name"
            ></Field>

            <ErrorMessage name="name" class="text-red-500 text-start" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-start font-bold text-main-color mb-2" for="phone"
              >{{ $t('form.phone') }}<span class="text-red-500">*</span></label
            >
            <Field
              v-model="formData.phone"
              placeholder="+993"
              class="p-4 w-full z-10 text-main-color bg-white rounded border focus:outline-none text-sm"
              type="phone"
              name="phone"
              id="phone"
            ></Field>

            <ErrorMessage name="phone" class="text-red-500 text-start" />
          </div>
          <div class="flex flex-col gap-1 relative">
            <label class="text-start font-bold text-main-color mb-2" for="address"
              >{{ $t('form.address') }}<span class="text-red-500">*</span></label
            >
            <div class="relative">
              <Field
                v-model="formData.address"
                @focus="onAddressFocus"
                @blur="onAddressBlur"
                class="p-4 w-full z-10 text-main-color bg-white rounded border focus:outline-none text-sm"
                type="address"
                name="address"
                id="address"
              ></Field>
              <div
                v-if="showAddresses"
                class="absolute top-14 border w-full h-52 z-20 bg-white shadow divide-y-2 overflow-auto"
              >
                <div
                  class="py-3 px-4 cursor-pointer"
                  v-for="address in addresses"
                  @click="setAddress(address)"
                  :key="address.id"
                >
                  {{ address.address }}
                </div>
              </div>
            </div>
            <ErrorMessage name="address" class="text-red-500 text-start" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-start font-bold text-main-color mb-2" for="note">{{
              $t('page.note')
            }}</label>
            <Field
              v-model="formData.note"
              class="p-4 w-full z-10 text-main-color bg-white rounded resize-none border focus:outline-none text-sm"
              as="textarea"
              cols="30"
              rows="6"
              name="note"
              id="note"
            ></Field>
            <ErrorMessage name="note" class="text-red-500 text-start" />
          </div>
          <div class="flex flex-col gap-1">
            <p class="text-start font-bold text-main-color mb-2">
              {{ $t('page.deliveryType') }}<span class="text-red-500">*</span>
            </p>
            <div class="flex flex-wrap gap-5">
              <div v-if="getFoodCart?.store?.delivery_price">
                <Field
                  v-model="formData.delivery_type"
                  class="hidden peer"
                  id="delivery_1"
                  type="radio"
                  name="delivery_type"
                  value="0"
                ></Field>
                <label
                  class="flex flex-col items-center gap-2 py-4 shadow-lg px-4 rounded cursor-pointer peer-checked:bg-main-color peer-checked:text-white peer-checked:border-main-color"
                  for="delivery_1"
                >
                  {{ $t('page.usual') }}
                </label>
              </div>
              <div v-if="getFoodCart?.store?.express_delivery_price">
                <Field
                  v-model="formData.delivery_type"
                  class="hidden peer"
                  id="delivery_2"
                  type="radio"
                  name="delivery_type"
                  value="1"
                ></Field>
                <label
                  class="flex flex-col items-center gap-2 py-4 shadow-lg px-4 rounded cursor-pointer peer-checked:bg-main-color peer-checked:text-white peer-checked:border-main-color"
                  for="delivery_2"
                >
                  {{ $t('page.express') }}
                </label>
              </div>
              <div v-if="getFoodCart?.store?.can_visit">
                <Field
                  v-model="formData.delivery_type"
                  class="hidden peer"
                  id="delivery_3"
                  type="radio"
                  name="delivery_type"
                  value="2"
                ></Field>
                <label
                  class="flex flex-col items-center gap-2 py-4 shadow-lg px-4 rounded cursor-pointer peer-checked:bg-main-color peer-checked:text-white peer-checked:border-main-color"
                  for="delivery_3"
                >
                  {{ $t('page.pickup') }}
                </label>
              </div>
              <ErrorMessage name="delivery_type" class="text-red-500" />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <p class="text-start font-bold text-main-color mb-2">
              {{ $t('page.paymentMethod') }}<span class="text-red-500">*</span>
            </p>
            <div class="flex flex-wrap gap-5">
              <div v-if="getFoodCart?.store?.accept_cash_payment">
                <Field
                  v-model="formData.payment_method"
                  class="hidden peer"
                  id="pay_1"
                  type="radio"
                  name="payment_method"
                  value="0"
                ></Field>
                <label
                  class="flex flex-col items-center gap-2 py-4 shadow-lg px-4 rounded cursor-pointer peer-checked:bg-main-color peer-checked:text-white peer-checked:border-main-color"
                  for="pay_1"
                >
                  <IconWallet class="peer-checked:[&>path]:stroke-white"></IconWallet>
                  {{ $t('page.cash') }}
                </label>
              </div>
              <div v-if="getFoodCart?.store?.accept_terminal_payment">
                <Field
                  v-model="formData.payment_method"
                  class="hidden peer"
                  id="pay_2"
                  type="radio"
                  name="payment_method"
                  value="1"
                ></Field>
                <label
                  class="flex flex-col items-center gap-2 py-4 shadow-lg px-4 rounded cursor-pointer peer-checked:bg-main-color peer-checked:text-white peer-checked:border-main-color"
                  for="pay_2"
                >
                  <IconCard class=""></IconCard>
                  {{ $t('page.terminal') }}
                </label>
              </div>
              <ErrorMessage name="payment_method" class="text-red-500" />
            </div>
          </div>
        </div>
        <div class="divide-y flex flex-col bg-white rounded h-max">
          <div class="p-5 flex justify-center">
            <p class="text-2xl text-main-color font-bold">{{ $t('nav.checkout') }}</p>
          </div>
          <div class="p-5">
            <div class="flex justify-between mb-5">
              <div class="flex flex-col gap-5">
                <p class="text-lg font-light text-main-color">{{ $t('page.overallPrice') }}</p>
                <p class="text-lg font-light text-main-color">{{ $t('page.delivery') }}</p>
                <p class="text-lg font-bold text-main-color">{{ $t('page.totalPrice') }}</p>
              </div>
              <div class="flex flex-col gap-5">
                <p class="text-lg font-light text-main-color">
                  {{ formatNumber(getFoodCart?.price) }} TMT
                </p>
                <p class="text-lg font-light text-main-color">{{ getDeliveryPrice }} TMT</p>
                <p class="text-lg font-bold text-main-color">{{ formatNumber(getTotal) }} TMT</p>
              </div>
            </div>
            <div class="mb-5 divide-y">
              <p class="mb-2 text-red-500">
                <span class="text-red-500">*</span>{{ $t('page.delivery_note') }}
              </p>
              <p v-if="getFoodCart?.store?.delivery_price" class="py-1 text-red-500">
                {{ $t('page.delivery_price', { price: getFoodCart?.store?.delivery_price }) }}
              </p>
              <p v-if="getFoodCart?.store?.express_delivery_price" class="py-1 text-red-500">
                {{
                  $t('page.express_price', { price: getFoodCart?.store?.express_delivery_price })
                }}
              </p>
              <p v-if="getFoodCart?.store?.min_delivery_price" class="py-1 text-red-500">
                {{ $t('page.min_order_plain', { price: getFoodCart?.store?.min_delivery_price }) }}
              </p>
              <p v-if="getFoodCart?.store?.free_delivery_after_price" class="py-1 text-red-500">
                {{
                  $t('page.freeAfterDelivery', {
                    price: getFoodCart?.store?.free_delivery_after_price,
                  })
                }}
              </p>
            </div>
            <div>
              <SubmitBtn
                type="submit"
                class="!w-full rounded text-white bg-main-color py-4 text-base font-bold"
              >
                {{ $t('page.completeCheckout') }}</SubmitBtn
              >
            </div>
          </div>
        </div>
      </div>
    </Form>
  </div>
</template>

<script>
import api from '@/api';
import { Field, Form, ErrorMessage } from 'vee-validate';
import { DELIVERY_TYPES, PAYMENT_METHODS } from '@/constants';
import IconWallet from '@icons/IconWallet.vue';
import IconCard from '@icons/IconCard.vue';
import IconGlobe from '@icons/IconGlobe.vue';
import Modal from '@cmp/Modal.vue';
import { checkoutSchema } from '@/schemas';
import { mapState } from 'pinia';
import { useAppStore } from '@/store';
import { formatNumber, getDay } from '@/helpers';
import IconClock from '@icons/IconClock.vue';

import FoodCheckoutCard from '@cmp/food/FoodCheckoutCard.vue';
export default {
  name: 'checkout',
  components: {
    Field,
    Form,
    ErrorMessage,
    IconWallet,
    IconCard,
    IconGlobe,
    Modal,
    FoodCheckoutCard,
    IconClock,
  },
  data() {
    return {
      loading: false,
      showAddresses: false,
      currentOrder: null,
      addresses: [],
      DELIVERY_TYPES: DELIVERY_TYPES,
      PAYMENT_METHODS: PAYMENT_METHODS,
      formData: {
        payment_method: null,
        delivery_type: '0',
        note: '',
        contact_info: null,
        name: null,
        phone: null,
        address: null,
      },
      checkoutSchema: checkoutSchema,
      showSuccess: false,
      receipt: {},
    };
  },
  watch: {
    'getFoodCart.price': {
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          if (this.getFoodCart.price < this.getFoodCart?.store?.min_delivery_price) {
            this.$router.push({ name: 'foodCart' });
          }
        }
      },
    },
  },
  methods: {
    formatNumber,
    getDay,
    setAddress(address) {
      this.formData.address = address.address;
      this.formData.phone = address.phone;
      this.formData.name = address.name;
    },
    onAddressFocus() {
      if (this.$store.authStatus == true) {
        this.showAddresses = true;
      }
    },
    onAddressBlur() {
      setTimeout(() => {
        this.showAddresses = false;
      }, 100);
    },
    async postContactInfo() {
      try {
        const body = {
          name: this.formData.name,
          address: this.formData.address,
          phone: this.formData.phone,
        };
        const response = await api.post(`users/contacts/`, body);
        return response.data.id;
      } catch (e) {
        console.log(e);
      }
    },
    async checkout() {
      try {
        const contactId = await this.postContactInfo();
        const body = {
          delivery_type: this.formData.delivery_type,
          payment_method: this.formData.payment_method,
          contact_info: contactId,
          note: this.formData.note,
        };

        const response = await api.post(`exhibition/orders/${this.currentOrder}/buy/`, body);
        this.receipt = response.data;
        await this.$store.getSessionFoodCart();
        this.showSuccess = true;
      } catch (e) {
        console.log(e);
      }
    },
    async getAddresses() {
      this.loading = true;
      try {
        const response = await api.get('users/contacts/');
        this.addresses = response.data;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    closeModal() {
      this.showSuccess = false;
      this.$router.push({ name: 'foodHomePage' });
    },
  },
  computed: {
    ...mapState(useAppStore, ['getFoodCartById']),
    getFoodCart() {
      return this.getFoodCartById(this.currentOrder);
    },
    getDeliveryPrice() {
      const deliveryType = Number(this.formData.delivery_type);
      const productPrice = Number(this.getFoodCart?.price || 0);
      const store = this.getFoodCart?.store || {};

      if (deliveryType === 2) {
        return 0;
      }

      if (deliveryType === 1) {
        return Number(store.express_delivery_price || 0);
      }

      if (deliveryType === 0) {
        const freeAfterPrice = store.free_delivery_after_price;

        if (
          freeAfterPrice !== null &&
          freeAfterPrice !== undefined &&
          freeAfterPrice !== '' &&
          productPrice >= Number(freeAfterPrice)
        ) {
          return 0;
        }

        return Number(store.delivery_price || 0);
      }

      return 0;
    },
    getTotal() {
      return Number(this.getDeliveryPrice || 0) + Number(this.getFoodCart?.price || 0);
    },
  },
  created() {
    this.currentOrder = this.$route.params.id;
    if (this.$store.authStatus == true) {
      this.getAddresses();
    }
  },
};
</script>
